/**
 * VireoHR - Manager Requests Screen
 * Ultra-Minimal Design - Approve/Reject Leave Requests
 */

import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { firestore } from '@/lib/firebase/client';


interface LeaveRequest {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: any;
}

export default function ManagerRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [filter, setFilter] = useState<'PENDING' | 'ALL'>('PENDING');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.tenantId) {
      fetchRequests();
    }
  }, [user, filter]);

  const fetchRequests = async () => {
    if (!user?.tenantId) return;

    try {
      setLoading(true);
      const requestsRef = collection(firestore, 'requests');
      
      let q;
      if (filter === 'PENDING') {
        q = query(
          requestsRef,
          where('tenantId', '==', user.tenantId),
          where('status', '==', 'PENDING')
        );
      } else {
        q = query(
          requestsRef,
          where('tenantId', '==', user.tenantId)
        );
      }

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LeaveRequest[];

      // Sort by date (most recent first)
      data.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      setRequests(data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    try {
      await updateDoc(doc(firestore, 'requests', requestId), {
        status: 'APPROVED',
        reviewedBy: user?.id,
        reviewedByName: user?.name,
        reviewedAt: new Date().toISOString(),
      });

      toast.success('Request approved');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleReject = async (requestId: string) => {
    try {
      await updateDoc(doc(firestore, 'requests', requestId), {
        status: 'REJECTED',
        reviewedBy: user?.id,
        reviewedByName: user?.name,
        reviewedAt: new Date().toISOString(),
      });

      toast.success('Request rejected');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout title="Requests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Requests">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-semibold text-gray-900">Leave Requests</h1>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'PENDING' | 'ALL')}
            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
          >
            <option value="PENDING">Pending</option>
            <option value="ALL">All</option>
          </select>
        </div>

        {requests.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">
                {filter === 'PENDING' ? 'No pending requests' : 'No leave requests'}
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {requests.map((request) => (
              <Card key={request.id}>
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{request.employeeName}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{formatDate(request.date)}</p>
                    </div>
                    <Badge
                      variant={
                        request.status === 'APPROVED' ? 'success' :
                        request.status === 'REJECTED' ? 'error' :
                        'warning'
                      }
                    >
                      {request.status}
                    </Badge>
                  </div>

                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Reason</p>
                    <p className="text-sm text-gray-900">{request.reason}</p>
                  </div>

                  {request.status === 'PENDING' && (
                    <div className="pt-2 border-t border-gray-100 flex gap-2">
                      <Button
                        onClick={() => handleApprove(request.id)}
                        variant="primary"
                        className="flex-1 text-sm"
                      >
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleReject(request.id)}
                        variant="outline"
                        className="flex-1 text-sm text-red-600 border-red-300 hover:bg-red-50"
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
